/**
 * Game Constants
 * Extracted from GameScene.ts to eliminate magic numbers
 */

import { Direction } from "../utils/Direction";

/**
 * Tile type indices used in level data
 */
export const TILE = {
  EMPTY: 0,
  WALL: 1,
  STAR: 2,
  BOMB: 3,
  KEY_GREEN: 4,
  KEY_RED: 5,
  KEY_ORANGE: 6,
  KEY_BLUE: 7,
  DOOR_GREEN: 8,
  DOOR_RED: 9,
  DOOR_ORANGE: 10,
  DOOR_BLUE: 11,
  PLAYER_START: 12,
  SAND: 13,
  BUG: 14,
  EXIT: 15,
  WATER: 16,
  FLIPPERS: 17,
  WATER_BLOCK: 18,
  HINT: 19,
  ARROW_UP: 20,
  ARROW_DOWN: 21,
  ARROW_LEFT: 22,
  ARROW_RIGHT: 23,
  SUCTION_BOOTS: 24,
  BUTTON: 25,
  BUTTON_DOOR: 26,
  BEE_HORIZONTAL: 27,
  BEE_VERTICAL: 28,
  CAT: 29,
  BALL: 30,
  ROTATION_BUTTON: 31,
  BUTTON_DOOR_OPEN: 32,
} as const;

/**
 * Helper function to check if a tile/entity is empty
 */
export const isEmpty = (tile: number | null): boolean => {
  return tile === TILE.EMPTY || tile === null;
};

/**
 * Helper type to check if a tile is an arrow tile
 */
export const isArrowTile = (tile: number): boolean => {
  return tile >= TILE.ARROW_UP && tile <= TILE.ARROW_RIGHT;
};

/**
 * Get the direction of an arrow tile
 * @throws Error if tile is not an arrow tile
 */
export const getArrowDirection = (tile: number): Direction => {
  switch (tile) {
    case TILE.ARROW_UP: return new Direction("up");
    case TILE.ARROW_DOWN: return new Direction("down");
    case TILE.ARROW_LEFT: return new Direction("left");
    case TILE.ARROW_RIGHT: return new Direction("right");
    default: throw new Error(`getArrowDirection called on non-arrow tile: ${tile}`);
  }
};

/**
 * Helper type to check if a tile is a key tile
 */
export const isKeyTile = (tile: number): boolean => {
  return tile >= TILE.KEY_GREEN && tile <= TILE.KEY_BLUE;
};

/**
 * Helper type to check if a tile is a door tile
 */
export const isDoorTile = (tile: number): boolean => {
  return tile >= TILE.DOOR_GREEN && tile <= TILE.DOOR_BLUE;
};

/**
 * UI color palette
 */
export const UI_COLORS = {
  // Panel colors (Mario brick/gold style)
  brickBrown: 0x63571c,
  panelHighlight: 0xe87c3c,
  panelShadow: 0xa03800,

  // Gold box colors
  goldBox: 0xdbcc80,
  goldBoxHighlight: 0xf8d878,
  goldBoxShadow: 0xc87800,
  goldText: 0x803800,

  // Button colors
  greenButton: 0x00a800,
  greenButtonHover: 0x00d800,
  playAgainButton: 0x448844,
  playAgainButtonHover: 0x66aa66,
  playAgainButtonStroke: 0x66aa66,

  // Overlay colors
  gameOverOverlay: 0x200000,
  hintOverlay: 0x402000,
  cameraBackground: 0x333333,

  // Explosion particle colors
  explosionOrange: 0xff6600,
  explosionRed: 0xff3300,
  explosionYellow: 0xffcc00,
  explosionDarkRed: 0xff0000,

  // Star outline
  starOutline: 0xcc9900,

  // Transparent (for debug buttons)
  transparent: 0xff0000,

  // Rainbow colors for victory text
  rainbow: {
    red: "#ff0000",
    orange: "#ff8800",
    yellow: "#ffff00",
    green: "#44ff44",
    blue: "#4488ff",
    purple: "#8844ff",
    magenta: "#ff44ff",
  },
} as const;

/**
 * Overlay screen dimensions (full-screen overlays like start/win/game over)
 */
export const OVERLAY_DIMENSIONS = {
  width: 1024,
  height: 768,
  centerX: 512,
  centerY: 384,
} as const;

/**
 * Get explosion particle colors array
 */
export const getExplosionColors = (): number[] => {
  return [
    UI_COLORS.explosionOrange,
    UI_COLORS.explosionRed,
    UI_COLORS.explosionYellow,
    UI_COLORS.explosionDarkRed,
  ];
};

/**
 * Get rainbow colors array
 */
export const getRainbowColors = (): string[] => {
  return Object.values(UI_COLORS.rainbow);
};
